import { AspectRatio, Box, Button, Container, Heading, Image, SimpleGrid } from '@chakra-ui/react'
import { NextPage } from 'next'
import { FiArrowLeft } from 'react-icons/fi'
import NextLink from 'next/link'
import SEO from 'components/SEO'
import { remark } from 'remark'
import strip from 'strip-markdown'
import { useEffect, useState } from 'react'
import SpecialistCard from 'components/SpecialistCard'
import sortBy from 'lodash.sortby'
import RenderHTML from 'components/RenderHTML'

interface SpecialityProps {
  speciality: Record<string, any>
}

const Speciality: NextPage<SpecialityProps> = ({ speciality }) => {
  const [description, setDescription] = useState('')
  const photo = speciality.attributes?.coverPhoto?.data
  const content = speciality.attributes.content
  const specialists = speciality.attributes?.specialists?.data
  const sortedSpecialists = sortBy(specialists, 'attributes.displayPriority').reverse()

  useEffect(() => {
    const fetchDescription = async () => {
      const stripped = String(await remark().use(strip).process(content))
      setDescription(stripped.substring(0, 256))
    }
    fetchDescription()
  }, [content])

  return (
    <Box>
      <SEO title={speciality.attributes.SEO.metaTitle} description={speciality.attributes.SEO.metaDescription} />
      {photo && (
        <AspectRatio ratio={[2, 3, 4, 5]} marginTop="1rem">
          <Image src={photo.attributes.url} alt={speciality.attributes.name} />
        </AspectRatio>
      )}
      <Container maxWidth="container.md" marginY="3rem">
        <NextLink href="/uslugi" passHref>
          <Button leftIcon={<FiArrowLeft />} variant="link">
            Usługi
          </Button>
        </NextLink>
        <Heading as="h1" size="lg" marginTop="1rem">
          {speciality.attributes.name} - Medwind Poznań
        </Heading>
        <RenderHTML>{speciality.attributes.content}</RenderHTML>
        {sortedSpecialists?.length > 0 && (
          <Box>
            <Heading size="md" marginTop="2rem">
              Zespół
            </Heading>
            <SimpleGrid columns={[1, 1, 2]} marginTop="1rem" gap="1rem">
              {sortedSpecialists?.map((specialist: any) => (
                <SpecialistCard key={specialist.id} specialist={specialist} />
              ))}
            </SimpleGrid>
          </Box>
        )}
      </Container>
    </Box>
  )
}

export async function getStaticPaths() {
  const URL = `${process.env.NEXT_PUBLIC_API_URL}/api/specialities`
  const res = await fetch(URL)
  const { data } = await res.json()
  const paths = data?.map((speciality: any) => ({ params: { slug: speciality.attributes.slug } }))
  return {
    paths,
    fallback: 'blocking'
  }
}

export async function getStaticProps({ params }: any) {
  const { slug } = params
  const URL = `${process.env.NEXT_PUBLIC_API_URL}/api/slugify/slugs/speciality/${slug}?populate=coverPhoto,specialists,specialists.photo,SEO`
  try {
    const res = await fetch(URL)
    const { data } = await res.json()
    return {
      props: {
        speciality: data
      },
      revalidate: 1
    }
  } catch {
    return {
      props: {},
      notFound: true
    }
  }
}

export default Speciality
